import type { AxiosInstance } from 'axios';
import type { CustomObjectField } from './custom-object';
import type { components, paths } from '../schema';
import type { DefaultFieldValue, FieldValue } from '../types/field-value';
import type { TimelineEntry } from '../types/timeline';
import type {
  PaginatedResponse,
  RequestOptions,
  RequestOptionsWithParams,
} from './types';
import { curry1 } from './utils';

type SchemaCreateActivityBody = Required<
  paths['/api/activities']['post']
>['requestBody']['content']['application/json'];

type SchemaUpdateActivityBody = Required<
  paths['/api/activities/{activity_identifier}']['patch']
>['requestBody']['content']['application/json'];

interface CreateActivityBody extends SchemaCreateActivityBody {
  name: string;
}

export interface UpdateActivityBody
  extends Omit<SchemaUpdateActivityBody, 'layout'> {
  layout?: ActivityLayoutItem[];
}

export type GetActivitiesParams =
  paths['/api/activities']['get']['parameters']['query'];

type CreateActivityFieldValue<FieldType, Name, Value> =
  Name extends DefaultFieldValue['name']
    ? {
        custom_field_type: FieldType;
        display_name: string;
        field_type: 'activity_custom_field';
        id: string;
        name: Name;
        value: Value;
      }
    :
        | {
            custom_field_type: null;
            display_name: string;
            field_type: FieldType;
            id: string;
            name: Name;
            value: Value;
          }
        | {
            custom_field_type: FieldType;
            display_name: string;
            field_type: 'activity_custom_field';
            id: string;
            name: Name;
            value: Value;
          };

type GenerateActivityFieldValues<T extends FieldValue> = T extends {
  field_type: infer FieldType;
  name: infer Name;
  value: infer Value;
}
  ? CreateActivityFieldValue<FieldType, Name, Value>
  : never;

type ActivityFieldValue = Pretty<GenerateActivityFieldValues<FieldValue>>;

export interface LoggedActivity
  extends Mutable<
    Omit<
      paths['/api/activities/{activity_identifier}/log-activity']['post']['responses']['200']['content']['application/json'],
      'fields'
    >
  > {
  fields: ActivityFieldValue[];
}

type LoggableActiviitesParams = Required<
  paths['/api/activities/loggable']['get']['parameters']
>['query'] & {
  custom_object_id?: string;
  page?: number;
  page_size?: number;
  search?: string;
};

type LoggableActivitiesResponse =
  paths['/api/activities/loggable']['get']['responses']['200']['content']['application/json'];

type SchemaActivityDetail = Mutable<
  paths['/api/activities/{activity_identifier}']['get']['responses']['200']['content']['application/json']
>;
type SchemaAssociatedObject = Mutable<
  SchemaActivityDetail['associated_objects'][number]
>;
interface AssociatedObjectCustomObject
  extends Omit<SchemaAssociatedObject['custom_object'], 'id' | 'name'> {
  id: string;
  name: string;
}
interface AssociatedObject
  extends Omit<SchemaAssociatedObject, 'custom_object'> {
  custom_object: AssociatedObjectCustomObject;
}

type ActivityLayoutItem =
  | {
      type: 'section';
      columns: number;
      label: string;
      internal_name: string;
    }
  | {
      type: 'field';
      columns: number;
      field_id: string;
    };

export type LoggedActivityEntry = Omit<
  components['schemas']['LogActivityList'],
  'fields' | 'logged_by' | 'scheduled_activity'
> & {
  fields_with_values: Record<
    string,
    Partial<ActivityField> & {
      value:
        | string
        | number
        | boolean
        | null
        | Record<string, any>
        | Record<string, any>[];
    }
  >;
  logged_by: {
    id: string;
    email: string;
    display_name: string;
  };
  scheduled_activity: {
    id: string;
    name: string;
  } | null;
};

export type GetLoggedActivitiesResponse = Mutable<
  paths['/api/activities/{activity_identifier}/responses']['post']['responses']['200']['content']['application/json']
>;

export type LoggedActivitiesParams =
  paths['/api/activities/{activity_identifier}/responses']['post']['parameters']['query'];

type SchemaActivity =
  paths['/api/activities']['get']['responses']['200']['content']['application/json']['results'][number];

export interface Activity extends Omit<SchemaActivity, 'layout'> {
  layout: ActivityLayoutItem[];
}

export interface ActivityDetail
  extends Omit<SchemaActivityDetail, 'associated_objects' | 'layout'> {
  associated_objects: AssociatedObject[];
  layout: ActivityLayoutItem[];
}

export type ActivityField = Mutable<
  Omit<
    paths['/api/activities/{activity_identifier}/fields']['get']['responses']['200']['content']['application/json'][number],
    'name' | 'properties' | 'relation' | 'custom_object_field'
  >
> & {
  name: string;
  custom_object_field?: CustomObjectField & {
    custom_object?: {
      id: string;
    };
  };
} & Pick<CustomObjectField, 'properties' | 'relation'>;

export type ActivityTimelineEntry = TimelineEntry<
  Record<string, any>,
  | 'activity_created'
  | 'activity_updated'
  | 'activity_deleted'
  | 'activity_field_created'
  | 'activity_field_updated'
  | 'activity_field_deleted'
>;

export type ActivityHistoryParams = Required<
  paths['/api/activities/{activity_identifier}/events-history']['get']['parameters']
>['query'];

export type ActivityHistory = Omit<
  paths['/api/activities/{activity_identifier}/events-history']['get']['responses']['200']['content']['application/json'],
  'results'
> & {
  results: ActivityTimelineEntry[];
};

export type ActivityUsage = Required<
  paths['/api/activities/{activity_identifier}/references']['get']['responses']['200']['content']['application/json']
>;

export type ScheduledActivity = Mutable<
  paths['/api/activities/scheduled-activity/search']['post']['responses']['200']['content']['application/json']['results'][number]
>;

export type SearchScheduledActivitiesBody =
  paths['/api/activities/scheduled-activity/search']['post']['requestBody']['content']['application/json'];

export type SearchScheduledActivitiesParams = Exclude<
  paths['/api/activities/scheduled-activity/search']['post']['parameters']['query'],
  undefined
>;

export const createActivity = async (
  instance: AxiosInstance,
  body: CreateActivityBody,
  options: RequestOptions = {}
): Promise<ActivityDetail> => {
  const res = await instance.post('/activities', body, options);

  return res.data;
};

export const createActivityField = async (
  instance: AxiosInstance,
  activityId: string,
  body: paths['/api/activities/{activity_identifier}/fields']['post']['requestBody']['content']['application/json'],
  options: RequestOptions = {}
): Promise<ActivityField> => {
  const res = await instance.post(
    `/activities/${activityId}/fields`,
    body,
    options
  );

  return res.data;
};

export const deleteScheduledActivities = async (
  instance: AxiosInstance,
  activityId: string,
  body: { send_calendar_sync_emails: boolean },
  options: RequestOptions = {}
): Promise<void> => {
  await instance.post(
    `/activities/${activityId}/delete-scheduled`,
    body,
    options
  );
};

export const getActivity = async (
  instance: AxiosInstance,
  id: string,
  options?: RequestOptions
): Promise<ActivityDetail> => {
  const res = await instance.get<ActivityDetail>(`/activities/${id}`, options);

  return res.data;
};

export const getActivities = async (
  instance: AxiosInstance,
  options: RequestOptionsWithParams<GetActivitiesParams> = {}
): Promise<PaginatedResponse<Activity>> => {
  const res = await instance.get('/activities', options);

  return res.data;
};

export const getActivityFields = async (
  instance: AxiosInstance,
  id: string,
  options?: RequestOptions
): Promise<ActivityField[]> => {
  const res = await instance.get<ActivityField[]>(
    `/activities/${id}/fields`,
    options
  );

  return res.data;
};

export const getLoggableActivities = async (
  instance: AxiosInstance,
  options: RequestOptionsWithParams<LoggableActiviitesParams> = {}
): Promise<LoggableActivitiesResponse> => {
  const res = await instance.get<LoggableActivitiesResponse>(
    `/activities/loggable`,
    options
  );

  return res.data;
};

export const getActivityHistory = async (
  instance: AxiosInstance,
  id: string,
  options: RequestOptionsWithParams<ActivityHistoryParams> = {}
): Promise<ActivityHistory> => {
  const res = await instance.get<ActivityHistory>(
    `/activities/${id}/events-history`,
    options
  );

  return res.data;
};

export const updateActivity = async (
  instance: AxiosInstance,
  id: string,
  body: UpdateActivityBody,
  options: RequestOptions = {}
): Promise<ActivityDetail> => {
  const res = await instance.patch(`/activities/${id}`, body, options);

  return res.data;
};

export const getLoggedActivities = async (
  instance: AxiosInstance,
  activityId: string,
  options: RequestOptionsWithParams<LoggedActivitiesParams> = {}
): Promise<GetLoggedActivitiesResponse> => {
  const res = await instance.post<GetLoggedActivitiesResponse>(
    `/activities/${activityId}/responses`,
    {},
    options
  );

  return res.data;
};

export const getActivityUsage = async (
  instance: AxiosInstance,
  activityId: string,
  options: RequestOptions = {}
): Promise<ActivityUsage> => {
  const res = await instance.get<ActivityUsage>(
    `/activities/${activityId}/references`,
    options
  );

  return res.data;
};

interface SearchScheduledActivitiesOptions
  extends RequestOptionsWithParams<SearchScheduledActivitiesParams> {
  body?: SearchScheduledActivitiesBody;
}

export const searchScheduledActivities = async (
  instance: AxiosInstance,
  options: SearchScheduledActivitiesOptions = {}
): Promise<PaginatedResponse<ScheduledActivity>> => {
  const { body = {}, ...opts } = options;

  const res = await instance.post(
    `/activities/scheduled-activity/search`,
    body,
    opts
  );

  return res.data;
};

export class ActivitiesService {
  public createActivity;
  public createActivityField;
  public deleteScheduledActivities;
  public getActivity;
  public getActivities;
  public getActivityFields;
  public getLoggableActivities;
  public getActivityHistory;
  public updateActivity;
  public getLoggedActivities;
  public getActivityUsage;
  public searchScheduledActivities;

  constructor(private instance: AxiosInstance) {
    this.createActivity = curry1(createActivity)(this.instance);
    this.createActivityField = curry1(createActivityField)(this.instance);
    this.deleteScheduledActivities = curry1(deleteScheduledActivities)(
      this.instance
    );
    this.getActivity = curry1(getActivity)(this.instance);
    this.getActivities = curry1(getActivities)(this.instance);
    this.getActivityFields = curry1(getActivityFields)(this.instance);
    this.getLoggableActivities = curry1(getLoggableActivities)(this.instance);
    this.getActivityHistory = curry1(getActivityHistory)(this.instance);
    this.updateActivity = curry1(updateActivity)(this.instance);
    this.getLoggedActivities = curry1(getLoggedActivities)(this.instance);
    this.getActivityUsage = curry1(getActivityUsage)(this.instance);
    this.searchScheduledActivities = curry1(searchScheduledActivities)(
      this.instance
    );
  }
}
